@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@500;600&family=Shippori+Mincho:wght@600;700&display=swap');

:root {
  font-family: "Avenir Next", "Noto Sans JP", "Hiragino Kaku Gothic ProN", sans-serif;
  color: #172027;
  background: #f2f0e9;
  font-synthesis: none;
  text-rendering: optimizeLegibility;
  --ink: #172027;
  --muted: #667078;
  --paper: #f2f0e9;
  --paper-bright: #faf9f5;
  --line: rgba(23, 32, 39, 0.16);
  --blue: #12538a;
  --sky: #62a9d5;
  --orange: #ef6645;
  --yellow: #edc761;
  --green: #3f806e;
}

* {
  box-sizing: border-box;
}

html {
  min-width: 320px;
  background: var(--paper);
  scroll-behavior: smooth;
}

body {
  margin: 0;
  min-width: 320px;
  min-height: 100vh;
  background:
    radial-gradient(circle at 78% 3%, rgba(98, 169, 213, 0.18), transparent 24rem),
    linear-gradient(rgba(23, 32, 39, 0.035) 1px, transparent 1px),
    linear-gradient(90deg, rgba(23, 32, 39, 0.035) 1px, transparent 1px),
    var(--paper);
  background-size: auto, 32px 32px, 32px 32px, auto;
}

button,
a {
  -webkit-tap-highlight-color: transparent;
}

button {
  font: inherit;
}

button:focus-visible,
a:focus-visible {
  outline: 3px solid var(--orange);
  outline-offset: 3px;
}

.app-shell {
  width: min(100%, 1440px);
  margin: 0 auto;
  border-inline: 1px solid var(--line);
  background: rgba(242, 240, 233, 0.72);
  overflow: hidden;
}

.site-header {
  min-height: 92px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 clamp(24px, 5vw, 72px);
  border-bottom: 1px solid var(--line);
  background: rgba(250, 249, 245, 0.82);
  backdrop-filter: blur(14px);
}

.brand {
  color: var(--ink);
  display: inline-flex;
  align-items: center;
  gap: 12px;
  text-decoration: none;
}

.brand-mark {
  width: 43px;
  height: 43px;
  flex: 0 0 auto;
  overflow: visible;
  fill: none;
  stroke: currentColor;
  stroke-width: 2.2;
  stroke-linecap: round;
  stroke-linejoin: round;
}

.brand-mark circle {
  fill: var(--orange);
  stroke: none;
}

.brand-jp {
  font-family: "Shippori Mincho", "Yu Mincho", serif;
  font-size: 1.52rem;
  font-weight: 700;
  letter-spacing: 0.08em;
}

.brand-en {
  padding-left: 12px;
  border-left: 1px solid var(--line);
  font-family: "IBM Plex Mono", monospace;
  font-size: 0.56rem;
  font-weight: 600;
  line-height: 1.25;
  letter-spacing: 0.12em;
  color: var(--muted);
}

.observation-status {
  display: inline-flex;
  align-items: center;
  gap: 9px;
  font-family: "IBM Plex Mono", monospace;
  font-size: 0.68rem;
  letter-spacing: 0.05em;
  color: var(--muted);
}

.live-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--green);
  box-shadow: 0 0 0 5px rgba(63, 128, 110, 0.12);
  animation: pulse 2.4s ease-in-out infinite;
}

.eyebrow {
  margin: 0 0 18px;
  font-family: "IBM Plex Mono", monospace;
  font-size: 0.67rem;
  font-weight: 600;
  letter-spacing: 0.13em;
  color: var(--blue);
}

.hero {
  position: relative;
  isolation: isolate;
  display: grid;
  grid-template-columns: minmax(0, 1.5fr) minmax(300px, 0.62fr);
  min-height: 560px;
  padding: clamp(72px, 9vw, 132px) clamp(24px, 7vw, 104px) clamp(72px, 8vw, 110px);
  border-bottom: 1px solid var(--line);
}

.hero::before {
  content: "";
  position: absolute;
  z-index: -1;
  width: 540px;
  height: 540px;
  right: -110px;
  top: -180px;
  border: 1px solid rgba(18, 83, 138, 0.18);
  border-radius: 50%;
  box-shadow:
    0 0 0 70px rgba(18, 83, 138, 0.035),
    0 0 0 140px rgba(18, 83, 138, 0.025);
}

.hero-copy {
  align-self: center;
  max-width: 820px;
}

.hero h1 {
  margin: 0;
  font-family: "Shippori Mincho", "Yu Mincho", serif;
  font-weight: 700;
  font-size: clamp(3rem, 6.4vw, 6.9rem);
  line-height: 1.16;
  letter-spacing: -0.045em;
  text-wrap: balance;
}

.hero h1::first-line {
  color: var(--blue);
}

.hero-lead {
  max-width: 660px;
  margin: 42px 0 0;
  padding-left: 22px;
  border-left: 3px solid var(--orange);
  font-size: clamp(0.98rem, 1.4vw, 1.12rem);
  font-weight: 500;
  line-height: 2;
  color: #49545c;
}

.today-forecast {
  position: relative;
  align-self: end;
  margin-left: clamp(20px, 4vw, 64px);
  padding: 22px;
  border: 1px solid var(--ink);
  background: var(--paper-bright);
  box-shadow: 9px 9px 0 var(--yellow);
}

.forecast-topline {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 13px;
  border-bottom: 1px solid var(--line);
  font-family: "IBM Plex Mono", monospace;
  font-size: 0.62rem;
  font-weight: 600;
  letter-spacing: 0.08em;
}

.forecast-topline span:last-child {
  color: var(--green);
}

.today-forecast > strong {
  display: block;
  margin-top: 18px;
  font-family: "IBM Plex Mono", monospace;
  font-size: clamp(4.5rem, 8vw, 7.5rem);
  font-weight: 500;
  line-height: 0.95;
  letter-spacing: -0.08em;
  color: var(--orange);
}

.forecast-caption {
  display: block;
  margin-top: 8px;
  font-size: 0.72rem;
  color: var(--muted);
}

.forecast-topic {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 16px;
  margin-top: 30px;
  padding-top: 15px;
  border-top: 1px solid var(--line);
}

.forecast-topic span {
  font-size: 0.7rem;
  color: var(--muted);
}

.forecast-topic b {
  text-align: right;
  font-family: "Shippori Mincho", serif;
  font-size: 1.1rem;
}

.signal-section {
  padding: clamp(68px, 8vw, 112px) clamp(24px, 5vw, 72px) clamp(86px, 10vw, 136px);
}

.section-heading {
  display: flex;
  align-items: end;
  justify-content: space-between;
  gap: 40px;
  margin-bottom: 34px;
}

.section-heading h2,
.methodology h2 {
  margin: 0;
  font-family: "Shippori Mincho", serif;
  font-size: clamp(2rem, 4vw, 4.1rem);
  line-height: 1.24;
  letter-spacing: -0.035em;
}

.scan-summary {
  margin: 0 0 7px;
  text-align: right;
  font-family: "IBM Plex Mono", monospace;
  font-size: 0.68rem;
  color: var(--muted);
}

.scan-summary p {
  margin: 0;
}

.scan-summary span {
  display: inline-block;
  margin-top: 7px;
  padding: 4px 8px;
  border: 1px solid var(--line);
  border-radius: 999px;
  color: var(--ink);
  background: rgba(250, 249, 245, 0.65);
}

.filters {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 28px;
  padding-top: 18px;
  border-top: 1px solid var(--ink);
}

.filters button {
  min-height: 42px;
  padding: 0 20px;
  border: 1px solid var(--line);
  border-radius: 100px;
  color: var(--ink);
  background: transparent;
  cursor: pointer;
  font-size: 0.8rem;
  font-weight: 600;
  transition: color 180ms ease, background 180ms ease, border-color 180ms ease, transform 180ms ease;
}

.filters button:hover {
  border-color: var(--ink);
  transform: translateY(-2px);
}

.filters button[aria-pressed="true"] {
  border-color: var(--ink);
  color: var(--paper-bright);
  background: var(--ink);
}

.partial-notice {
  margin: 0 0 24px;
  padding: 12px 16px;
  border-left: 3px solid var(--yellow);
  background: rgba(237, 199, 97, 0.13);
  font-size: 0.82rem;
}

.signal-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  border-top: 1px solid var(--line);
  border-left: 1px solid var(--line);
}

.signal-card {
  --category: var(--blue);
  position: relative;
  display: flex;
  min-width: 0;
  min-height: 510px;
  flex-direction: column;
  padding: 24px 24px 0;
  border-right: 1px solid var(--line);
  border-bottom: 1px solid var(--line);
  background: rgba(250, 249, 245, 0.76);
  transition: background 220ms ease, transform 220ms ease, box-shadow 220ms ease;
}

.signal-card::before {
  content: "";
  position: absolute;
  inset: 0 0 auto;
  height: 4px;
  background: var(--category);
  transform: scaleX(0.28);
  transform-origin: left;
  transition: transform 260ms ease;
}

.signal-card:hover {
  z-index: 1;
  background: var(--paper-bright);
  transform: translateY(-6px);
  box-shadow: 0 18px 34px rgba(23, 32, 39, 0.09);
}

.signal-card:hover::before {
  transform: scaleX(1);
}

.category-youtube { --category: var(--orange); }
.category-ai { --category: var(--green); }

.signal-card__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
}

.signal-card__meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.rank {
  font-family: "IBM Plex Mono", monospace;
  font-size: 0.75rem;
  color: var(--muted);
}

.category-label,
.discovery-label {
  padding: 5px 9px;
  border-radius: 99px;
  font-size: 0.66rem;
  font-weight: 700;
}

.category-label {
  border: 1px solid color-mix(in srgb, var(--category) 55%, transparent);
  color: var(--category);
}

.discovery-label {
  border: 1px solid var(--ink);
  color: var(--paper-bright);
  background: var(--ink);
}

.score-dial {
  --score: 0deg;
  position: relative;
  display: grid;
  place-content: center;
  width: 78px;
  aspect-ratio: 1;
  flex: 0 0 auto;
  border-radius: 50%;
  background: conic-gradient(var(--category) var(--score), rgba(23, 32, 39, 0.09) 0);
}

.score-dial::after {
  content: "";
  position: absolute;
  inset: 5px;
  border-radius: inherit;
  background: var(--paper-bright);
}

.score-value,
.score-unit {
  position: relative;
  z-index: 1;
  text-align: center;
  font-family: "IBM Plex Mono", monospace;
}

.score-value {
  font-size: 1.42rem;
  line-height: 1;
}

.score-unit {
  margin-top: 3px;
  font-size: 0.48rem;
  color: var(--muted);
}

.signal-card__body {
  flex: 1;
}

.signal-card h2 {
  margin: 32px 0 13px;
  font-family: "Shippori Mincho", serif;
  font-size: clamp(1.55rem, 2.3vw, 2.15rem);
  line-height: 1.3;
  letter-spacing: -0.025em;
}

.reason {
  min-height: 3.6em;
  margin: 0;
  color: #536068;
  font-size: 0.82rem;
  line-height: 1.8;
}

.metrics {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  margin: 26px 0;
  padding: 16px 0;
  border-block: 1px solid var(--line);
}

.metrics div + div {
  padding-left: 14px;
  border-left: 1px solid var(--line);
}

.metrics dt {
  margin-bottom: 7px;
  font-family: "IBM Plex Mono", monospace;
  font-size: 0.52rem;
  letter-spacing: 0.08em;
  color: var(--muted);
}

.metrics dd {
  margin: 0;
  font-family: "IBM Plex Mono", monospace;
  font-size: 1.05rem;
}

.source-post {
  margin: 0;
  padding: 0;
  border: 0;
}

.source-post p {
  display: -webkit-box;
  min-height: 4.7em;
  margin: 0;
  overflow: hidden;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  font-size: 0.78rem;
  line-height: 1.65;
  color: #3f4b53;
}

.source-post footer {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  margin-top: 12px;
  font-family: "IBM Plex Mono", monospace;
  font-size: 0.6rem;
  color: var(--muted);
}

.source-link {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 56px;
  margin: 24px -24px 0;
  padding: 0 24px;
  border-top: 1px solid var(--line);
  color: var(--ink);
  text-decoration: none;
  font-size: 0.74rem;
  font-weight: 700;
  transition: color 180ms ease, background 180ms ease;
}

.source-link svg {
  width: 20px;
  fill: none;
  stroke: currentColor;
  stroke-width: 1.5;
}

.source-link:hover {
  color: var(--paper-bright);
  background: var(--category);
}

.empty-state {
  padding: 70px 24px;
  border: 1px solid var(--line);
  text-align: center;
  background: rgba(250, 249, 245, 0.65);
}

.empty-state p {
  color: var(--muted);
}

.empty-state button,
.primary-button {
  min-height: 46px;
  padding: 0 22px;
  border: 1px solid var(--ink);
  color: var(--paper-bright);
  background: var(--ink);
  cursor: pointer;
  font-weight: 700;
}

.methodology {
  display: grid;
  grid-template-columns: minmax(260px, 0.75fr) minmax(0, 1.25fr);
  gap: clamp(48px, 9vw, 140px);
  padding: clamp(80px, 10vw, 140px) clamp(24px, 7vw, 104px);
  color: #f7f4e8;
  background:
    radial-gradient(circle at 10% 80%, rgba(98, 169, 213, 0.17), transparent 30rem),
    #18232b;
}

.methodology .eyebrow {
  color: var(--sky);
}

.methodology ol {
  margin: 0;
  padding: 0;
  list-style: none;
}

.methodology li {
  display: grid;
  grid-template-columns: 54px 1fr;
  gap: 18px;
  padding: 25px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.18);
}

.methodology li:last-child {
  border-bottom: 1px solid rgba(255, 255, 255, 0.18);
}

.methodology li > span {
  font-family: "IBM Plex Mono", monospace;
  color: var(--sky);
}

.methodology li b {
  font-family: "Shippori Mincho", serif;
  font-size: 1.3rem;
}

.methodology li p {
  margin: 8px 0 0;
  color: rgba(247, 244, 232, 0.66);
  font-size: 0.84rem;
  line-height: 1.7;
}

.site-footer {
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 48px;
  min-height: 150px;
  padding: 34px clamp(24px, 5vw, 72px);
  background: var(--paper-bright);
}

.footer-brand .brand-mark {
  width: 34px;
  height: 34px;
}

.footer-brand .brand-jp {
  font-size: 1.18rem;
}

.site-footer p {
  max-width: 610px;
  margin: 0 auto;
  color: var(--muted);
  font-size: 0.68rem;
  line-height: 1.7;
}

.site-footer > span {
  font-family: "IBM Plex Mono", monospace;
  font-size: 0.58rem;
  color: var(--muted);
}

.state-screen {
  display: grid;
  place-items: center;
  align-content: center;
  min-height: 100vh;
  padding: 32px;
  text-align: center;
}

.state-screen .brand-mark {
  width: 70px;
  height: 70px;
  margin-bottom: 24px;
  color: var(--blue);
}

.state-screen h1 {
  margin: 0;
  font-family: "Shippori Mincho", serif;
  font-size: clamp(2.4rem, 7vw, 5rem);
}

.state-screen > p:last-child {
  color: var(--muted);
}

.loading-line {
  width: min(340px, 70vw);
  height: 2px;
  margin: 34px 0 16px;
  overflow: hidden;
  background: var(--line);
}

.loading-line span {
  display: block;
  width: 45%;
  height: 100%;
  background: var(--orange);
  animation: scan 1.3s ease-in-out infinite;
}

.error-state p:not(.eyebrow) {
  max-width: 520px;
  line-height: 1.8;
  color: var(--muted);
}

.error-state .primary-button {
  margin-top: 20px;
}

@keyframes pulse {
  50% { opacity: 0.5; box-shadow: 0 0 0 9px rgba(63, 128, 110, 0); }
}

@keyframes scan {
  from { transform: translateX(-110%); }
  to { transform: translateX(240%); }
}

@media (max-width: 1040px) {
  .hero {
    grid-template-columns: 1fr;
    gap: 58px;
  }

  .today-forecast {
    width: min(420px, 100%);
    margin-left: auto;
  }

  .signal-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 720px) {
  .app-shell {
    border-inline: 0;
  }

  .site-header {
    min-height: 74px;
    padding-inline: 18px;
  }

  .brand-jp {
    font-size: 1.2rem;
  }

  .brand-en,
  .observation-status span:last-child {
    display: none;
  }

  .brand-mark {
    width: 36px;
    height: 36px;
  }

  .hero {
    min-height: auto;
    padding: 70px 20px 80px;
  }

  .hero::before {
    width: 330px;
    height: 330px;
    right: -170px;
    top: -110px;
  }

  .hero h1 {
    font-size: clamp(2.75rem, 14vw, 4.4rem);
    line-height: 1.2;
  }

  .hero-lead {
    margin-top: 30px;
    padding-left: 16px;
    font-size: 0.91rem;
  }

  .today-forecast {
    margin: 0;
    box-shadow: 6px 6px 0 var(--yellow);
  }

  .signal-section {
    padding: 72px 16px 96px;
  }

  .section-heading {
    display: block;
  }

  .scan-summary {
    margin-top: 22px;
    text-align: left;
  }

  .filters {
    flex-wrap: nowrap;
    overflow-x: auto;
    padding-bottom: 5px;
  }

  .filters button {
    flex: 0 0 auto;
  }

  .signal-grid {
    grid-template-columns: 1fr;
  }

  .signal-card {
    min-height: 490px;
  }

  .methodology {
    grid-template-columns: 1fr;
    padding: 82px 22px;
  }

  .site-footer {
    grid-template-columns: 1fr;
    gap: 22px;
    padding: 44px 22px;
  }

  .site-footer p {
    margin: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  html { scroll-behavior: auto; }
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }
}
